/**
 * Trainer Service for handling communication with the trainer API
 * Provides methods for generating and managing Q&A pairs
 */
import api from './api';

const trainerService = {
  /**
   * Generate Q&A pairs from a document or knowledge base
   * @param {string} document - The document text to generate Q&A pairs from (for provided_text mode)
   * @param {number} numQuestions - Number of questions to generate
   * @param {string} contentSource - Source of content: 'provided_text' or 'knowledge_base'
   * @param {string} query - Query for knowledge base search (for knowledge_base mode)
   * @returns {Promise} - Promise containing the generated Q&A pairs
   */
  generateQAPairs: async (document, numQuestions = 5, contentSource = 'provided_text', query = '') => {
    try {
      const requestData = {
        document,
        num_questions: numQuestions,
        content_source: contentSource
      };
      
      // Add query parameter if using knowledge base
      if (contentSource === 'knowledge_base') {
        requestData.query = query;
      }
      
      console.log('Generating Q&A pairs:', { 
        contentSource, 
        document: document ? document.substring(0, 100) + '...' : 'N/A', 
        query: query || 'N/A',
        numQuestions 
      });
      
      const response = await api.post('/trainer/generate-qa-pairs', requestData);
      
      console.log('Q&A pairs generation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error generating Q&A pairs:', error);
      throw error;
    }
  },
  
  /**
   * Suggest similar questions for a given question
   * @param {string} question - The question to find similar questions for
   * @param {number} topK - Number of similar questions to return
   * @returns {Promise} - Promise containing the similar questions
   */
  suggestSimilarQuestions: async (question, topK = 3) => {
    try {
      console.log('Suggesting similar questions:', { question, topK });
      
      const response = await api.post('/trainer/suggest-similar-questions', {
        question,
        top_k: topK
      });
      
      console.log('Similar questions response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error suggesting similar questions:', error);
      throw error;
    }
  },
  
  /**
   * Validate an answer for a question
   * @param {string} question - The question
   * @param {string} answer - The answer to validate
   * @returns {Promise} - Promise containing the validation result
   */
  validateAnswer: async (question, answer) => {
    try {
      console.log('Validating answer:', { question, answer: answer.substring(0, 100) + '...' });
      
      const response = await api.post('/trainer/validate-answer', {
        question,
        answer
      });
      
      console.log('Answer validation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error validating answer:', error);
      throw error;
    }
  },
  
  /**
   * Create or update a Q&A pair
   * @param {string} question - The question
   * @param {string} answer - The answer
   * @param {number} qaPairId - Optional ID for updating an existing Q&A pair
   * @param {string} tags - Optional tags for the Q&A pair
   * @param {string} source - Optional source of the Q&A pair
   * @returns {Promise} - Promise containing the created/updated Q&A pair
   */
  createOrUpdateQAPair: async (question, answer, qaPairId = null, tags = null, source = null) => {
    try {
      console.log('Creating/updating Q&A pair:', { question, answer: answer.substring(0, 100) + '...', qaPairId, tags, source });
      
      const response = await api.post('/trainer/create-or-update-qa-pair', {
        question,
        answer,
        qa_pair_id: qaPairId,
        tags,
        source
      });
      
      console.log('Q&A pair creation/update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating/updating Q&A pair:', error);
      throw error;
    }
  },
  
  /**
   * Fine-tune the model with selected Q&A pairs
   * @param {Array<number>} qaPairIds - Array of Q&A pair IDs to use for fine-tuning
   * @returns {Promise} - Promise containing the fine-tuning result
   */
  fineTune: async (qaPairIds) => {
    try {
      console.log('Fine-tuning model with Q&A pairs:', qaPairIds);
      
      const response = await api.post('/trainer/fine-tune', {
        qa_pair_ids: qaPairIds
      });
      
      console.log('Fine-tuning response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fine-tuning model:', error);
      throw error;
    }
  },
  
  /**
   * Submit feedback for a Q&A pair
   * @param {number} qaPairId - The ID of the Q&A pair
   * @param {number} rating - Rating (1-5)
   * @param {string} comments - Feedback comments
   * @param {string} userId - User ID submitting the feedback
   * @returns {Promise} - Promise containing the feedback submission result
   */
  submitFeedback: async (qaPairId, rating, comments, userId) => {
    try {
      console.log('Submitting feedback:', { qaPairId, rating, comments, userId });
      
      const response = await api.post('/trainer/feedback', {
        qa_pair_id: qaPairId,
        rating,
        comments,
        user_id: userId
      });
      
      console.log('Feedback submission response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  },
  
  /**
   * Get feedback for a Q&A pair
   * @param {number} qaPairId - The ID of the Q&A pair
   * @returns {Promise} - Promise containing the feedback for the Q&A pair
   */
  getFeedbackForQA: async (qaPairId) => {
    try {
      console.log('Getting feedback for Q&A pair:', qaPairId);
      
      const response = await api.get(`/trainer/feedback/${qaPairId}`);
      
      console.log('Feedback response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting feedback:', error);
      throw error;
    }
  },
  
  /**
   * Get feedback analytics
   * @returns {Promise} - Promise containing feedback analytics
   */
  getFeedbackAnalytics: async () => {
    try {
      console.log('Getting feedback analytics');
      
      const response = await api.get('/trainer/feedback-analytics');
      
      console.log('Feedback analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting feedback analytics:', error);
      throw error;
    }
  }
};

export default trainerService;

